import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { SharedModule } from '../../shared.module';
import { UiModalComponent } from '../../ui-modal/ui-modal.component';

/**
 * App-wide click-to-zoom image popup.
 * Listens for clicks on any <img> in the app and opens it inside the existing
 * app-ui-modal, skipping static UI icons served from /assets/ (back buttons,
 * copy icons, etc.) and any image explicitly opted out via [data-no-preview].
 *
 * Registered on the capture phase (not via @HostListener, which is bubble-only)
 * and stops propagation when it opens the popup. Several list rows (e.g.
 * delivery-history) wrap thumbnails in a [routerLink] on an ancestor <td>; without
 * capture + stopPropagation, clicking the thumbnail would navigate the row instead
 * of previewing the image.
 */
@Component({
    selector: 'app-image-preview',
    imports: [SharedModule],
    templateUrl: './image-preview.component.html',
    styleUrl: './image-preview.component.scss'
})
export class ImagePreviewComponent implements OnInit, OnDestroy {
  @ViewChild('imagePreviewModal') imagePreviewModal!: UiModalComponent;
  imageSrc = '';
  imageAlt = '';

  private readonly handleDocumentClick = (event: MouseEvent): void => {
    const target = event.target as HTMLElement;
    if (!target || target.tagName !== 'IMG') {
      return;
    }

    const img = target as HTMLImageElement;

    if (img.closest('[data-no-preview]') || img.src.includes('/assets/')) {
      return;
    }

    event.stopPropagation();
    event.preventDefault();

    this.imageSrc = img.src;
    this.imageAlt = img.alt || 'Preview';
    this.imagePreviewModal.show();
  };

  ngOnInit(): void {
    document.addEventListener('click', this.handleDocumentClick, true);
  }

  ngOnDestroy(): void {
    document.removeEventListener('click', this.handleDocumentClick, true);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.imagePreviewModal?.hide();
  }
}
